import { mutatePurchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { users } from "@/api/users/useUsersQuery";
import { customFunctions } from "@/config/customFunction";
import { itemTypeColor } from "@/constants/items";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { OrderDetailForm } from "@/features/order/form/DetailForm";
import { OrderIncomingForm } from "@/features/order/form/OrderIncomingForm";
import { OrderItemsCreateForm } from "@/features/orderItems/form/CreateForm";
import { OrderItemsUpdateForm } from "@/features/orderItems/form/updarteForm";
import { PartnerDetailForm } from "@/features/partner/form/DetailForm";
import { DeliveredForm } from "@/features/purchaseOrderItems/delivered/DeliveredForm";
import { PurchaseOrderItemsDetailForm } from "@/features/purchaseOrderItems/form/PurchaseOrderItemDetailForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { useLoader } from "@/hooks/useLoader";
import DatetimeUtil from "@/utils/dateTimeUtil";
import { translateWithPartnerType } from "@/utils/partnerType";
import { purchaseOrderItemStatusColor, setPurchaseOrderItemStatus } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { setToLocaleString } from "@/utils/unitMark";
import { ActionIcon, Avatar, Badge, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  AuthSignupPost201ResponseEquipmentPurchaseOrderItem,
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrderItemsGet200Response,
  PurchaseOrderItemsWithItemGradeGet200ResponseRowsInner,
  ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconEdit,
  IconFilterPlus,
  IconPlus,
  IconRecycle,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

const userAuth = customFunctions.ADD_ORDER_USER_AUTH;

export const OrderItemsPage = () => {
  const { data: userInfo } = useQuery(users.who({}));
  const userCode = userInfo?.data?.code;
  const partnerType = userInfo?.data?.partner?.type;
  const { t } = useTranslation();
  const { setLoading, LoadingOverlay } = useLoader();
  const { openModal, closeModal } = useModal();
  // const [isBeta] = useLocalStorage<boolean>({
  //   key: "isBeta",
  //   defaultValue: false, // 기본값은 모든 컬럼이 체크됨
  // });

  const { query, setQuery, selectedRows, setSelectedRows, refetch, resetQueryStrings, data } =
    useStandardLayout();

  const selectedData = (data as AxiosResponse<PurchaseOrderItemsGet200Response, any>)?.data?.rows
    ?.filter((item) => selectedRows.has(item.id))
    ?.map((item) => item);

  useEffect(() => {
    if (query.length === 0) {
      // query가 비어 있을 때만 실행
      setQuery([
        { direction: { $eq: "OUTBOUND" } },
        {
          ...(userAuth
            ? {}
            : {
                $or: [{ creatorUserCode: userCode }, { partnerCode: userCode }],
              }),
        },
      ]);
    }

    if (userInfo?.data?.externalUserType) {
      setQuery([{ partnerCode: { $eq: userInfo?.data?.partner?.code } }]);
    }
  }, [query, userCode, userAuth]);

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest) =>
      mutatePurchaseOrderItems
        .delete(params)
        .mutationFn(params as any | ScmApiPurchaseOrderItemsPurchaseOrderItemIdDeleteRequest),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
          }),
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: () => {
        customAlert(
          t("message.deleteFail", {
            item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
          }),
          t("common.deleteFail"),
          "red",
        );
      },
    },
  );

  const columns: readonly Column<AuthSignupPost201ResponseEquipmentPurchaseOrderItem>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "partner.name",
      name: t("partner.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <PartnerDetailForm partnerCode={formatterProps.row.partnerCode} />,
                null,
                t("common.detail", { item: t("partner.partner") }),
              )
            }
          >
            {formatterProps.row.partner?.name}
          </LinkAnchor>
        );
      },
    },
    {
      key: "purchaseOrderCode",
      name: translateWithPartnerType(t, "order.code", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <OrderDetailForm purchaseOrderId={formatterProps?.row.purchaseOrderId} />,
                null,
                t("common.detail", {
                  item: translateWithPartnerType(t, "order.order", partnerType),
                }),
              )
            }
          >
            {formatterProps.row.purchaseOrderCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemType",
      name: "품목군",
      sortable: true,
      resizable: true,
      formatter: ({ row }: { row: PurchaseOrderItemsWithItemGradeGet200ResponseRowsInner }) => {
        return (
          <Badge size="lg" color={itemTypeColor[row?.itemType as keyof typeof itemTypeColor]}>
            {t(row?.itemType as string)}
          </Badge>
        );
      },
    },
    {
      key: "itemName",
      name: "품목명",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={formatterProps?.row?.itemCode} />,
                null,
                "품목 상세",
              )
            }
          >
            {formatterProps.row.itemName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "gradeName",
      name: "Grade",
      sortable: true,
      resizable: true,
    },
    {
      key: "itemCategoryName",
      name: "Color",
      sortable: true,
      resizable: true,
    },
    {
      key: "abstractItemCode",
      name: "형태",
      sortable: true,
      resizable: true,
    },
    {
      key: "status",
      name: translateWithPartnerType(t, "orderItems.status", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Badge
            variant="filled"
            color={
              purchaseOrderItemStatusColor[
                formatterProps.row.status as keyof typeof purchaseOrderItemStatusColor
              ]
            }
          >
            {setPurchaseOrderItemStatus(formatterProps.row.status ?? "")}
          </Badge>
        );
      },
    },
    {
      key: "quantity",
      name: t("orderItems.quantity"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <Flex justify="flex-end">{setToLocaleString(formatterProps.row.quantity)}</Flex>;
      },
    },
    {
      key: "inputEligibleQuantity",
      name: t("orderItems.inputEligibleQuantity"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex justify="flex-end">
            {setToLocaleString(formatterProps.row.inputEligibleQuantity)}
          </Flex>
        );
      },
    },
    {
      key: "sumOfStockPlanQuantity",
      name: translateWithPartnerType(t, "orderItems.sumOfStockPlanQuantity", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex justify="flex-end">
            {setToLocaleString(formatterProps.row.sumOfStockPlanQuantity)}
          </Flex>
        );
      },
    },
    {
      key: "sumOfDeliveredQuantity",
      name: translateWithPartnerType(t, "orderItems.sumOfDeliveredQuantity", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex h="100%" justify="flex-end" align="center">
            {formatterProps.row.sumOfDeliveredQuantity === "0" ? (
              <Text>{formatterProps.row.sumOfDeliveredQuantity}</Text>
            ) : (
              <LinkAnchor
                onClick={() =>
                  openModal(
                    <DeliveredForm purchaseOrderItemID={formatterProps?.row?.id} />,
                    null,
                    t("common.detail", { item: t("delivery.deliveryComplete") }),
                  )
                }
              >
                {setToLocaleString(formatterProps.row.sumOfDeliveredQuantity)}
              </LinkAnchor>
            )}
          </Flex>
        );
      },
    },
    {
      key: "orderDate",
      name: translateWithPartnerType(t, "order.orderDate", partnerType),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.orderDate !== null
              ? DatetimeUtil(formatterProps.row.orderDate as string)
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "createdAt",
      name: t("orderItems.createdAt"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.createdAt !== null
              ? DatetimeUtil(formatterProps.row.createdAt as string)
              : ""}
          </Flex>
        );
      },
    },
    {
      key: "deliveryDeadline",
      name: t("orderItems.deliveryDeadline"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.deliveryDeadline !== null
              ? timeUtil(formatterProps.row.deliveryDeadline as string)
              : ""}
          </Flex>
        );
      },
    },
    ...(partnerType !== PartnersGet200ResponseRowsInnerTypeEnum.PURCHASE
      ? [
          {
            key: "options",
            name: t("common.action"),
            sortable: true,
            resizable: true,
            formatter: (formatterProps: any) => {
              const detailPurchaseOrderItemActionView = () => {
                openModal(
                  <PurchaseOrderItemsDetailForm
                    purchaseOrderItemId={formatterProps.row.id as number}
                    pageKey="orderItems"
                  />,
                  null,
                  t("common.detail", {
                    item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
                  }),
                );
              };

              const updatePurchaseOrderActionView = () => {
                openModal(
                  <OrderItemsUpdateForm formatterProps={formatterProps?.row} />,
                  null,
                  t("common.edit", {
                    item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
                  }),
                  true,
                );
              };

              const deletePurchaseOrderAction = () => {
                openModal(
                  <ConfirmForm
                    message={t("message.rowDelete")}
                    yesCallback={() =>
                      deleteMutate({ purchaseOrderItemId: formatterProps?.row.id as number })
                    }
                    noCallback={() => {}}
                    commonCallback={() => closeModal(null)}
                  />,
                  null,
                  t("common.delete", {
                    item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
                  }),
                );
              };

              return (
                <Flex w="100%" h="100%" justify="center" align="center" gap="md">
                  <ActionIcon
                    onClick={detailPurchaseOrderItemActionView}
                    variant="subtle"
                    color="blue"
                  >
                    <IconSearch />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    onClick={updatePurchaseOrderActionView}
                    color="yellow"
                  >
                    <IconEdit />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => deletePurchaseOrderAction()}
                    color="red"
                  >
                    <IconTrash />
                  </ActionIcon>
                </Flex>
              );
            },
          },
        ]
      : []),
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        t("message.deleteSelect", {
          item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
        }),
        t("common.deleteFail", {
          item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
        }),
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ purchaseOrderItemId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      t("common.delete", {
        item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
      }),
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <LoadingOverlay />
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          {partnerType !== PartnersGet200ResponseRowsInnerTypeEnum.PURCHASE ? (
            <Button
              leftIcon={<IconPlus />}
              onClick={() =>
                openModal(
                  <OrderItemsCreateForm />,
                  null,
                  t("common.additional", {
                    item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
                  }),
                )
              }
            >
              {t("orderItems.create")}
            </Button>
          ) : null}
          {!customFunctions.ADD_ORDER_ITEMS_INCOMING_BUTTON && (
            <Button
              leftIcon={<IconPlus />}
              color="green"
              disabled={[...selectedRows].length === 0}
              onClick={() => {
                openModal(
                  <OrderIncomingForm orderItems={selectedData} setLoading={setLoading} />,
                  null,
                  t("inventory.incoming"),
                ).then((result) => {
                  if (result) {
                    // 로직이 정상적으로 처리되면 선택된 행을 초기화
                    setSelectedRows((): Set<any> => new Set());
                  }
                });
              }}
            >
              {t("inventory.incoming")}
            </Button>
          )}
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {t("common.list", {
              item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
            })}
          </Text>
          <Text fz="xs">{t("common.subTitle", { item: t("orderItems.description") })}</Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<
            PurchaseOrderItemsGet200Response,
            AuthSignupPost201ResponseEquipmentPurchaseOrderItem
          >
            columns={columns}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<PurchaseOrderItemsGet200Response> />
      </Flex>
    </Flex>
  );
};
